import React, { useEffect, useMemo, useRef, useState } from "react";
import { Field, FormikProvider, useFormik } from "formik";
import { Spin } from "antd";
import { Col, OverlayTrigger, Popover, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import CustomSelect from "../../../CustomControls/CustomSelect";
import { SVGICON } from "../../../constant/theme";
import { post } from "../../../helpers/api_helper";
import {
  POST_GetMachinesDropDownList,
  POST_GetRawDataGrid,
  POST_GetRawDataParameterList,
  POST_RawDataDelete,
  POST_RawDataInsert,
} from "../../../helpers/url_helper";
import {
  delete_Record,
  errorMessage,
  successMessage,
  warningMessage,
} from "../CommonFunctions";

const paramKey = (type, idEncrypt) => `${type}_${idEncrypt}`;

// small grey context after a parameter name (the sub-parameters a child feeds / the parent), same look as the lists
const contextStyle = { fontSize: "0.72rem", color: "#9aa0a6" };

// compact vertical spacing for the cells of the grid body
const cellStyle = {
  verticalAlign: "middle",
  paddingTop: "0.2rem",
  paddingBottom: "0.2rem",
};

// One grid row = TC + test + target parameter (+ its parent when the target is a sub-parameter or a sub child parameter).
// A sub child parameter is one row even when it feeds several sub-parameters.
const rowKeyOf = (r) =>
  [
    r.InwardMaterialIDEncrypt,
    r.TestMasterIDEncrypt,
    paramKey(r.ParameterType, r.ParameterIDEncrypt),
    r.IsSubParameter || r.IsSubChildParameter
      ? paramKey(r.ParentParameterType, r.ParentParameterIDEncrypt)
      : "",
    r.IsSubChildParameter ? "C" : "",
  ].join("|");

// A saved reading is addressed by its row key + reading number
const savedKeyOf = (v) =>
  [
    v.InwardMaterialIDEncrypt,
    v.TestMasterIDEncrypt,
    paramKey(v.ParameterType, v.ParameterIDEncrypt),
    v.ParentParameterIDEncrypt
      ? paramKey(v.ParentParameterType, v.ParentParameterIDEncrypt)
      : "",
    v.IsSubChildParameter ? "C" : "",
    v.ResultCount,
  ].join("|");

const TestingRawDataTab = () => {
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const [isLoading, setIsLoading] = useState(false);
  const [machineOptions, setMachineOptions] = useState([]);
  const [parameterList, setParameterList] = useState([]);
  const [gridRows, setGridRows] = useState([]);
  const [savedValues, setSavedValues] = useState([]);
  // values typed but not saved yet: { [rowKey]: { [readingNo]: "text" } }
  const [inputs, setInputs] = useState({});

  const formik = useFormik({
    initialValues: { ParameterKey: "", MachineIDEncrypt: "" },
  });
  const { ParameterKey, MachineIDEncrypt } = formik.values;

  const parameterOptions = useMemo(
    () =>
      parameterList.map((p) => ({
        label: p.ParameterName,
        value: paramKey(p.ParameterType, p.ParameterIDEncrypt),
      })),
    [parameterList],
  );
  const selectedParameter = parameterList.find(
    (p) => paramKey(p.ParameterType, p.ParameterIDEncrypt) === ParameterKey,
  );

  const savedMap = useMemo(() => {
    const map = {};
    savedValues.forEach((v) => {
      map[savedKeyOf(v)] = v;
    });
    return map;
  }, [savedValues]);

  // the same parameter can appear under more than one parent in a test; only then show the parent name
  const duplicateCount = useMemo(() => {
    const counts = {};
    gridRows.forEach((r) => {
      const k = [
        r.InwardMaterialIDEncrypt,
        r.TestMasterIDEncrypt,
        paramKey(r.ParameterType, r.ParameterIDEncrypt),
      ].join("|");
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [gridRows]);

  // TC No and Test cells are merged over consecutive rows that belong to the same TC / the same TC + test.
  // A span of 0 means the cell is covered by the one above and must not be rendered.
  const spans = useMemo(() => {
    const tcKey = (r) => r.InwardMaterialIDEncrypt;
    const testKey = (r) => `${r.InwardMaterialIDEncrypt}|${r.TestMasterIDEncrypt}`;
    const tc = new Array(gridRows.length).fill(0);
    const test = new Array(gridRows.length).fill(0);
    let tcStart = 0;
    let testStart = 0;
    gridRows.forEach((r, i) => {
      if (i === 0 || tcKey(r) !== tcKey(gridRows[i - 1])) tcStart = i;
      if (i === 0 || testKey(r) !== testKey(gridRows[i - 1])) testStart = i;
      tc[tcStart] += 1;
      test[testStart] += 1;
    });
    return { tc, test };
  }, [gridRows]);

  const hasMultiReading = useMemo(
    () => gridRows.some((r) => (r.ReadingCount || 1) > 1),
    [gridRows],
  );
  const hasDeletable = useMemo(
    () => savedValues.some((v) => v.CanDelete),
    [savedValues],
  );

  const authPayload = () => ({
    CompanyIDEncrypt: AuthUser?.CompanyIDEncrypt,
    BranchIDEncrypt: AuthUser?.BranchIDEncrypt,
    LoginIDEncrypt: AuthUser?.LoginIDEncrypt,
  });

  const getMachinesDDL = async () => {
    await post(POST_GetMachinesDropDownList, {
      CompanyIDEncrypt: AuthUser?.CompanyIDEncrypt,
      BranchIDEncrypt: AuthUser?.BranchIDEncrypt,
    })
      .then((response) => {
        if (response?.IsSuccess) {
          setMachineOptions(
            response?.MachinesDropDownList?.map((item) => ({
              label: item.MachineName,
              value: item.MachineIDEncrypt,
            })) ?? [],
          );
        }
      })
      .catch((error) => console.log(error));
  };

  // parameters mapped to the selected equipment (and present in the allocated tests of the logged-in employee)
  const latestMachineRef = useRef("");
  const getParameterList = async (machineIDEncrypt) => {
    setIsLoading(true);
    await post(POST_GetRawDataParameterList, {
      MachineIDEncrypt: machineIDEncrypt,
      ...authPayload(),
    })
      .then((response) => {
        if (latestMachineRef.current !== machineIDEncrypt) return; // equipment changed meanwhile
        if (response?.IsSuccess) {
          setParameterList(response?.ListOfParameter ?? []);
          if ((response?.ListOfParameter ?? []).length === 0) {
            warningMessage(response?.Message);
          }
        } else {
          errorMessage(response?.Message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  // every allocated TC / test row for the selected parameter, together with the readings already saved
  const latestParameterRef = useRef("");
  const loadGrid = async (parameter) => {
    const key = paramKey(parameter.ParameterType, parameter.ParameterIDEncrypt);
    setIsLoading(true);
    await post(POST_GetRawDataGrid, {
      ParameterType: parameter.ParameterType,
      ParameterIDEncrypt: parameter.ParameterIDEncrypt,
      ...authPayload(),
    })
      .then((response) => {
        if (latestParameterRef.current !== key) return; // parameter changed meanwhile
        if (response?.IsSuccess) {
          setGridRows(response?.ListOfRow ?? []);
          setSavedValues(response?.ListOfValue ?? []);
          if ((response?.ListOfRow ?? []).length === 0) {
            warningMessage(response?.Message);
          }
        } else {
          setGridRows([]);
          setSavedValues([]);
          errorMessage(response?.Message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getMachinesDDL();
  }, []);

  const resetGrid = () => {
    setGridRows([]);
    setSavedValues([]);
    setInputs({});
  };

  const handleMachineChange = (option) => {
    const value = option?.value ?? "";
    latestMachineRef.current = value;
    latestParameterRef.current = "";
    formik.setFieldValue("MachineIDEncrypt", value);
    // a different equipment means a different parameter list: reset everything below it
    formik.setFieldValue("ParameterKey", "");
    setParameterList([]);
    resetGrid();
    if (value) {
      getParameterList(value);
    }
  };

  const handleParameterChange = (option) => {
    const value = option?.value ?? "";
    latestParameterRef.current = value;
    formik.setFieldValue("ParameterKey", value);
    resetGrid();
    const parameter = parameterList.find(
      (p) => paramKey(p.ParameterType, p.ParameterIDEncrypt) === value,
    );
    if (parameter) {
      loadGrid(parameter);
    }
  };

  const handleValueChange = (rowKey, readingNo, value) =>
    setInputs((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), [readingNo]: value },
    }));

  // ---- save --------------------------------------------------------------
  const handleSave = async () => {
    if (!selectedParameter) {
      warningMessage("Select parameter.");
      return;
    }
    if (!MachineIDEncrypt) {
      warningMessage("Select equipment.");
      return;
    }

    // every typed, not-yet-saved reading becomes one entry carrying its own parameter
    const payloadRows = [];
    gridRows.forEach((row) => {
      const rk = rowKeyOf(row);
      for (let n = 1; n <= Math.max(row.ReadingCount || 1, 1); n++) {
        if (savedMap[`${rk}|${n}`]) continue;
        const value = inputs[rk]?.[n] ?? "";
        if (String(value).trim() === "") continue;
        payloadRows.push({
          InwardMaterialIDEncrypt: row.InwardMaterialIDEncrypt,
          TestMasterIDEncrypt: row.TestMasterIDEncrypt,
          ParameterType: row.ParameterType,
          ParameterIDEncrypt: row.ParameterIDEncrypt,
          IsSubParameter: row.IsSubParameter,
          IsSubChildParameter: !!row.IsSubChildParameter,
          ParentParameterType:
            row.IsSubParameter || row.IsSubChildParameter ? row.ParentParameterType : 0,
          ParentParameterIDEncrypt:
            row.IsSubParameter || row.IsSubChildParameter
              ? row.ParentParameterIDEncrypt
              : "",
          ResultCount: n,
          ResultValue: value,
        });
      }
    });

    if (payloadRows.length === 0) {
      warningMessage("Enter at least one result value.");
      return;
    }

    setIsLoading(true);
    await post(POST_RawDataInsert, {
      MachineIDEncrypt,
      CompanyIDEncrypt: AuthUser?.CompanyIDEncrypt,
      BranchIDEncrypt: AuthUser?.BranchIDEncrypt,
      CreatedByEncrypt: AuthUser?.LoginIDEncrypt,
      ListOfRow: payloadRows,
    })
      .then(async (response) => {
        if (response?.IsSuccess) {
          successMessage(response?.Message);
          setInputs({});
          await loadGrid(selectedParameter);
        } else {
          errorMessage(response?.Message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const handleDelete = (testingResultLogBookIDEncrypt) => {
    delete_Record(
      POST_RawDataDelete,
      {
        TestingResultLogBookIDEncrypt: testingResultLogBookIDEncrypt,
        CompanyIDEncrypt: AuthUser?.CompanyIDEncrypt,
        BranchIDEncrypt: AuthUser?.BranchIDEncrypt,
        ModifiedByEncrypt: AuthUser?.LoginIDEncrypt,
      },
      setIsLoading,
      () => loadGrid(selectedParameter),
    );
  };

  const canEnter = !!selectedParameter && !!MachineIDEncrypt;

  return (
    <Spin spinning={isLoading} tip="Loading...">
      <FormikProvider value={formik}>
        <Row className="me-0">
          <Col xs={12} md={6} lg={4}>
            <Field
              name="MachineIDEncrypt"
              component={CustomSelect}
              label="Equipment"
              required
              placeholder="Select equipment"
              options={machineOptions}
              onChange={handleMachineChange}
              menuPosition="fixed"
            />
          </Col>
          <Col xs={12} md={6} lg={4}>
            <Field
              name="ParameterKey"
              component={CustomSelect}
              label={
                <>
                  Parameter (Individual / Parent){" "}
                  <OverlayTrigger
                    trigger="click"
                    rootClose
                    placement="bottom"
                    overlay={
                      <Popover id="rdlb-parameter-info">
                        <Popover.Body>
                          Select an individual or parent parameter. For a
                          parent, its sub-parameters are listed as rows, with
                          one input per reading. Sub-parameter readings are
                          entered only through their parent. The sub child
                          parameters that feed a sub-parameter formula are
                          listed after them, once each, and the value is saved
                          for every sub-parameter they feed.
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <span
                      className="rdlb-info"
                      role="button"
                      tabIndex={0}
                      aria-label="About parameter selection"
                    >
                      <i
                        className="fa fa-info-circle"
                        style={{ cursor: "pointer", color: "#9698ab" }}
                      ></i>
                    </span>
                  </OverlayTrigger>
                </>
              }
              required
              placeholder="Select parameter"
              options={parameterOptions}
              onChange={handleParameterChange}
              isDisabled={!MachineIDEncrypt}
              menuPosition="fixed"
            />
          </Col>
        </Row>

        {canEnter && (
          <>
            <div className="table-responsive active-projects style-1 shorting rdlb-grid">
              <div className="dataTables_wrapper no-footer">
                <table className="table dataTable no-footer mb-0">
                  <thead>
                    <tr>
                      <th width="12%" className="rdlb-tc">
                        TC No
                      </th>
                      <th width="25%">Test</th>
                      <th width="25%">Parameter</th>
                      <th style={{ textAlign: "left" }}>Result Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gridRows.length > 0 ? (
                      gridRows.map((row, rowIndex) => {
                        const rk = rowKeyOf(row);
                        const readingCount = Math.max(row.ReadingCount || 1, 1);
                        const showParent =
                          row.IsSubParameter &&
                          duplicateCount[
                            [
                              row.InwardMaterialIDEncrypt,
                              row.TestMasterIDEncrypt,
                              paramKey(row.ParameterType, row.ParameterIDEncrypt),
                            ].join("|")
                          ] > 1;
                        return (
                          <tr key={rk}>
                            {spans.tc[rowIndex] > 0 && (
                              <td
                                className="fw-bold"
                                rowSpan={spans.tc[rowIndex]}
                                style={cellStyle}
                              >
                                {row.TCNo}
                              </td>
                            )}
                            {spans.test[rowIndex] > 0 && (
                              <td
                                rowSpan={spans.test[rowIndex]}
                                style={cellStyle}
                              >
                                {row.TestName}
                              </td>
                            )}
                            <td style={cellStyle}>
                              {row.ParameterName}
                              {row.IsSubChildParameter && row.SubParameterNames && (
                                <span style={contextStyle}>
                                  {" "}
                                  <i
                                    className="fa-solid fa-right-long me-1"
                                    title="Feeds the sub-parameter"
                                    aria-label="Feeds the sub-parameter"
                                  ></i>
                                  {row.SubParameterNames}
                                </span>
                              )}
                              {showParent && (
                                <span style={contextStyle}>
                                  {" "}
                                  <i
                                    className="fa-solid fa-sitemap me-1"
                                    title="Parent parameter"
                                    aria-label="Parent parameter"
                                  ></i>
                                  {row.ParentParameterName}
                                </span>
                              )}
                            </td>
                            <td style={{ ...cellStyle, textAlign: "left" }}>
                              <div
                                className="d-flex flex-wrap align-items-center"
                                style={{ columnGap: "0.75rem", rowGap: "0.4rem" }}
                              >
                                {Array.from(
                                  { length: readingCount },
                                  (_, i) => i + 1,
                                ).map((n) => {
                                  const saved = savedMap[`${rk}|${n}`];
                                  return (
                                    <div key={n} className="d-flex flex-column">
                                      <div className="d-flex align-items-center">
                                        {hasMultiReading && (
                                          <span className="text-nowrap rdlb-label">
                                            {readingCount > 1 ? `R${n}` : ""}
                                          </span>
                                        )}
                                        <input
                                          type="text"
                                          className="form-control form-control-sm rdlb-input"
                                          maxLength={100}
                                          value={
                                            saved
                                              ? saved.ResultValue
                                              : (inputs[rk]?.[n] ?? "")
                                          }
                                          disabled={!!saved}
                                          title={
                                            saved
                                              ? `Equipment: ${saved.MachineName}\nEntered On: ${saved.EnteredOn}`
                                              : undefined
                                          }
                                          onChange={(e) =>
                                            handleValueChange(rk, n, e.target.value)
                                          }
                                          aria-label={`${row.ParameterName} result ${n}`}
                                        />
                                        {hasDeletable && (
                                          <span className="d-inline-flex justify-content-center rdlb-delete-slot">
                                            {saved?.CanDelete && (
                                              <Link
                                                to="#"
                                                className="icon-box icon-box-md"
                                                title="Delete"
                                                onClick={() =>
                                                  handleDelete(
                                                    saved.TestingResultLogBookIDEncrypt,
                                                  )
                                                }
                                              >
                                                {SVGICON.DeleteDanger}
                                              </Link>
                                            )}
                                          </span>
                                        )}
                                      </div>
                                      {saved && (
                                        <div
                                          className={`text-muted rdlb-caption${
                                            hasMultiReading ? " rdlb-caption-indent" : ""
                                          }`}
                                        >
                                          <div>{saved.MachineName}</div>
                                          <div>{saved.EnteredOn}</div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center text-muted">
                          No TC No is allocated to you for the selected
                          parameter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rdlb-savebar">
              <button
                type="button"
                className="btn btn-primary btn-sm me-2"
                onClick={handleSave}
                disabled={gridRows.length === 0 || isLoading}
              >
                <i className="fa-solid fa-floppy-disk"></i> Save
              </button>
            </div>
          </>
        )}
      </FormikProvider>
    </Spin>
  );
};

export default TestingRawDataTab;
